import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";

export interface StaticTrackingState {
  promoEnabled: boolean;
  qrEnabled: boolean;
  staticCouponCode: string;
  qrCodeLink: string;
}

export interface TrackingCampaignData {
  couponType?: string | null;
  staticCouponCode?: string | null;
  isTrackableCoupon?: number | null;
  qrCodeLink?: string | null;
}

const URL_PATTERN = /^https?:\/\/\S+\.\S{2,}$/i;
export const isValidUrl = (value: string) => URL_PATTERN.test(value.trim());

export interface UseStaticTrackingOptions<T extends TrackingCampaignData> {
  campaignId?: string;
  campaignData: T | null;
  /**
   * Write-through to the campaign cache so the persisted tracking fields stay in
   * sync with the loaded campaign (other readers + a full reload see the change).
   */
  onPersisted?: (patch: TrackingCampaignData) => void;
}

export interface StaticTracking {
  state: StaticTrackingState;
  update: (patch: Partial<StaticTrackingState>) => void;
  persistCoupon: () => Promise<unknown>;
  persistQr: () => Promise<unknown>;
  isValid: boolean;
}

/**
 * Static-only Tracking for H2H/D2D (epic T2): two independent toggles mapped onto
 * the existing single static fields — no per-recipient codes, variable files, or
 * placeholders. Promo → couponType:"static" + staticCouponCode; QR → qrCodeLink.
 *
 * Owned by the builder mount (above the shell) so the form survives step switches:
 * the step itself unmounts when you leave it, and re-hydrating from the cached
 * campaign would otherwise revert any unsaved edit on return.
 */
export const useStaticTracking = <T extends TrackingCampaignData>({
  campaignId,
  campaignData,
  onPersisted,
}: UseStaticTrackingOptions<T>): StaticTracking => {
  const [state, setState] = useState<StaticTrackingState>({
    promoEnabled: false,
    qrEnabled: false,
    staticCouponCode: "",
    qrCodeLink: "",
  });

  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!campaignData || !campaignId || hydratedFor.current === campaignId) return;
    hydratedFor.current = campaignId;
    // A campaign carries a promo iff it's flagged trackable with the static type —
    // never infer it from a leftover code string, which can linger after the toggle
    // was cleared and would falsely re-check the box.
    const hasCoupon = campaignData.isTrackableCoupon === 1 && campaignData.couponType === "static";
    const hasQr = !!campaignData.qrCodeLink;
    setState({
      promoEnabled: hasCoupon,
      qrEnabled: hasQr,
      staticCouponCode: campaignData.staticCouponCode ?? "",
      qrCodeLink: campaignData.qrCodeLink ?? "",
    });
  }, [campaignData, campaignId]);

  const interacted = useRef(false);
  const update = useCallback((patch: Partial<StaticTrackingState>) => {
    interacted.current = true;
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Autosave is optimistic: the cache write-through reflects immediately for a snappy
  // draft-edit feel, and a failed mutation is logged rather than surfaced (this is a
  // best-effort draft autosave with no error-state design yet — matches ADM tracking).
  const persistCoupon = useCallback(() => {
    if (!campaignId) return Promise.resolve();
    const enabled = state.promoEnabled;
    const couponType = enabled ? "static" : null;
    const staticCouponCode = enabled ? state.staticCouponCode || null : null;
    const isTrackableCoupon = enabled ? 1 : 0;
    onPersisted?.({ isTrackableCoupon, couponType, staticCouponCode });
    return updateCampaignExtraData({ campaignId, isTrackableCoupon, couponType, staticCouponCode }).catch((err) =>
      console.error("tracking: failed to persist coupon", err)
    );
  }, [campaignId, state.promoEnabled, state.staticCouponCode, onPersisted]);

  const persistQr = useCallback(() => {
    if (!campaignId) return Promise.resolve();
    const qrCodeLink = state.qrEnabled ? state.qrCodeLink || null : null;
    onPersisted?.({ qrCodeLink });
    return updateCampaignExtraData({ campaignId, qrCodeLink }).catch((err) =>
      console.error("tracking: failed to persist QR link", err)
    );
  }, [campaignId, state.qrEnabled, state.qrCodeLink, onPersisted]);

  // Toggling a feature on/off persists immediately (clears the field when off); the
  // field value itself persists on blur. Skips the initial hydration pass.
  // persist* is intentionally omitted from the deps: it is re-created by useCallback
  // on the same render that flips promoEnabled/qrEnabled, so the effect always closes
  // over a current version — adding it would only re-fire the autosave needlessly.
  useEffect(() => {
    if (interacted.current) void persistCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.promoEnabled]);

  useEffect(() => {
    if (interacted.current) void persistQr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.qrEnabled]);

  const isValid = useMemo(() => {
    if (state.promoEnabled && !state.staticCouponCode.trim()) return false;
    if (state.qrEnabled && !isValidUrl(state.qrCodeLink)) return false;
    return true;
  }, [state]);

  return { state, update, persistCoupon, persistQr, isValid };
};
